/**
 * GoogleCallback — Vague 105
 *
 * Handles the redirect coming back from Google after `/auth/google` was set as
 * the OAuth `redirect_uri`. Reads `?code=...&state=...` from the URL, sends
 * the code to POST /api/auth/google, then navigates to the role-specific
 * destination returned by the backend (`redirect_path`).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 * `redirect_uri` is computed at runtime as `window.location.origin + '/auth/google'`.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import api, { setToken } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import OrgPicker from '../components/OrgPicker';

export default function GoogleCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);
  const [orgChoice, setOrgChoice] = useState(null);
  // Wave 1u9 — guard against React.StrictMode double-invocation that would
  // consume the OAuth code/state twice and fail the second call.
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;
    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error');

    if (oauthError) {
      setError(`Google a refusé la demande : ${oauthError}`);
      return;
    }
    if (!code) {
      setError('Code OAuth manquant — relancez la connexion depuis /login.');
      return;
    }

    // CSRF check — verify state matches the nonce we stored before redirecting.
    const expectedState = sessionStorage.getItem('google_oauth_state');
    sessionStorage.removeItem('google_oauth_state');
    if (state && expectedState && state !== expectedState) {
      setError('Signature d\'état invalide (CSRF). Relancez la connexion.');
      return;
    }

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
    const redirectUri = `${window.location.origin}/auth/google`;

    (async () => {
      try {
        // Vague 108 — if we came from an invite's Google button, pass the
        // invite_token so the backend can consume it & provision the user.
        const inviteToken = sessionStorage.getItem('google_oauth_invite_token');
        sessionStorage.removeItem('google_oauth_invite_token');
        // Vague 112 — "link" intent from /profil (user already authenticated).
        const linkMode = sessionStorage.getItem('google_oauth_link_mode') === '1';
        sessionStorage.removeItem('google_oauth_link_mode');

        const body = { code, redirect_uri: redirectUri };
        if (inviteToken) body.invite_token = inviteToken;
        if (linkMode) body.link_mode = true;
        const { data } = await api.post('/auth/google', body);
        if (data?.linked) {
          // Link flow — keep existing session, refresh user state, go back.
          setUser?.(data.user || null);
          const after = sessionStorage.getItem('google_oauth_after');
          sessionStorage.removeItem('google_oauth_after');
          navigate(after || '/app/profil', { replace: true });
          return;
        }
        if (data?.requires_org_choice && data?.challenge) {
          // Multi-org — let the user pick the organization to enter.
          setOrgChoice(data);
          return;
        }
        if (data?.session_token) {
          setToken(data.session_token);
          setUser?.(data.user || null);
          // If the link flow stashed a return-to URL (e.g. coming from Profile),
          // honour that. Otherwise use the role-based redirect_path.
          const after = sessionStorage.getItem('google_oauth_after');
          sessionStorage.removeItem('google_oauth_after');
          navigate(after || data.redirect_path || '/app', { replace: true });
        } else {
          setError('Réponse inattendue du serveur — pas de session_token reçu.');
        }
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || 'Connexion impossible';
        setError(detail);
      }
    })();
  }, [params, navigate, setUser]);

  if (orgChoice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="w-full max-w-[420px]">
          <OrgPicker choice={orgChoice} onBack={() => navigate('/login', { replace: true })} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="max-w-[420px] text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#FEF2F2] flex items-center justify-center mx-auto">
            <AlertCircle className="w-7 h-7 text-[#EF4444]" />
          </div>
          <h1 className="mt-4 text-[20px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Connexion Google impossible
          </h1>
          <p className="mt-2 text-[13.5px] text-[#6B6C85]">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            data-testid="google-callback-back-btn"
            className="mt-5 h-10 px-5 rounded-xl ofm-gradient text-white text-[13px] font-semibold"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center" data-testid="google-callback-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--ofm-primary)] mx-auto" />
        <p className="mt-4 text-[14px] text-[#6B6C85]">Finalisation de la connexion…</p>
      </div>
    </div>
  );
}
